import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { formatPrice } from "@/lib/api";

export default function AdminDocument() {
  const { type, id } = useParams();
  const [order, setOrder] = useState(null);
  const [company, setCompany] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    Promise.all([
      api.post(`/orders/${id}/${type === "factura" ? "invoice" : "awb"}`),
      api.get("/company"),
    ])
      .then(([o, c]) => {
        setOrder(o.data);
        setCompany(c.data);
        setTimeout(() => window.print(), 700);
      })
      .catch(() => setError("Nu s-a putut încărca documentul."));
  }, [type, id]);

  if (error) return <div className="p-10 text-center text-red-600">{error}</div>;
  if (!order || !company) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-sky-200 border-t-sky-600" />
      </div>
    );
  }

  const c = order.customer;
  const isInvoice = type === "factura";
  const vatRate = company.vat_rate || 0;
  const totalWithVat = order.total;
  const base = vatRate ? totalWithVat / (1 + vatRate / 100) : totalWithVat;
  const vat = totalWithVat - base;

  return (
    <div className="mx-auto max-w-3xl bg-white p-10 text-slate-800 print:p-0" data-testid="admin-document">
      <style>{`@media print { @page { margin: 16mm; } .no-print { display:none !important; } body { background:#fff; } }`}</style>

      <div className="no-print mb-6 flex justify-end gap-3">
        <button onClick={() => window.print()} className="rounded-full bg-sky-600 px-5 py-2 font-semibold text-white hover:bg-sky-500" data-testid="doc-print">
          Printează / Salvează PDF
        </button>
      </div>

      {/* Header */}
      <div className="flex items-start justify-between border-b-2 border-slate-900 pb-5">
        <div>
          <div className="flex items-center gap-2">
            <img src="/logo-icon.png" alt="Mimaso" className="h-9 w-auto" />
            <span className="text-2xl font-black">mimaso</span>
          </div>
          <p className="mt-3 text-sm font-bold">{company.legal_name}</p>
          <p className="text-xs text-slate-500">{company.address}</p>
          <p className="text-xs text-slate-500">CUI: {company.cui} · Reg. Com.: {company.reg_com}</p>
          <p className="text-xs text-slate-500">Tel: {company.phone} · {company.email}</p>
          {isInvoice && <p className="text-xs text-slate-500">IBAN: {company.iban} · {company.bank}</p>}
        </div>
        <div className="text-right">
          <h1 className="text-2xl font-black uppercase tracking-tight">{isInvoice ? "Factură" : "AWB"}</h1>
          <p className="mt-2 text-sm font-bold">
            {isInvoice ? order.invoice_number : order.awb_number}
          </p>
          <p className="text-xs text-slate-500">
            Data: {new Date(isInvoice ? order.invoice_date : order.awb_date).toLocaleDateString("ro-RO")}
          </p>
          <p className="text-xs text-slate-500">Comandă #{order._id.slice(-6).toUpperCase()}</p>
        </div>
      </div>

      {/* Recipient */}
      <div className="mt-6 grid grid-cols-2 gap-6">
        <div>
          <p className="text-xs font-bold uppercase tracking-wider text-slate-400">{isInvoice ? "Client" : "Destinatar"}</p>
          <p className="mt-1 font-bold">{c.name}</p>
          <p className="text-sm text-slate-600">{c.phone}</p>
          {c.email && <p className="text-sm text-slate-600">{c.email}</p>}
          <p className="text-sm text-slate-600">{c.address}</p>
          <p className="text-sm text-slate-600">{c.city}, {c.county}</p>
        </div>
        {!isInvoice && (
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Expeditor</p>
            <p className="mt-1 font-bold">{company.legal_name}</p>
            <p className="text-sm text-slate-600">{company.address}</p>
            <p className="text-sm text-slate-600">{company.phone}</p>
          </div>
        )}
        {isInvoice && (
          <div className="text-right">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Modalitate de plată</p>
            <p className="mt-1 font-bold">{order.payment_method}</p>
          </div>
        )}
      </div>

      {/* Items */}
      <table className="mt-7 w-full text-sm">
        <thead>
          <tr className="border-b border-slate-300 text-left text-slate-500">
            <th className="py-2">Produs</th>
            <th className="py-2 text-center">Cant.</th>
            <th className="py-2 text-right">Preț</th>
            <th className="py-2 text-right">Valoare</th>
          </tr>
        </thead>
        <tbody>
          {order.items.map((it, idx) => (
            <tr key={idx} className="border-b border-slate-100">
              <td className="py-2">{it.name}</td>
              <td className="py-2 text-center">{it.quantity}</td>
              <td className="py-2 text-right">{formatPrice(it.price)}</td>
              <td className="py-2 text-right font-semibold">{formatPrice(it.price * it.quantity)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Totals */}
      <div className="mt-5 flex justify-end">
        <div className="w-64 space-y-1.5 text-sm">
          {isInvoice && vatRate > 0 && (
            <>
              <div className="flex justify-between text-slate-600"><span>Bază impozabilă</span><span>{formatPrice(base)}</span></div>
              <div className="flex justify-between text-slate-600"><span>TVA ({vatRate}%)</span><span>{formatPrice(vat)}</span></div>
            </>
          )}
          {!isInvoice && (
            <div className="flex justify-between text-slate-600"><span>Ramburs de încasat</span><span>{formatPrice(order.total)}</span></div>
          )}
          <div className="flex justify-between border-t-2 border-slate-900 pt-2 text-base font-black">
            <span>TOTAL</span><span>{formatPrice(order.total)}</span>
          </div>
        </div>
      </div>

      {!isInvoice && (
        <div className="mt-6 rounded-lg border border-slate-300 p-4 text-sm">
          <p><strong>Conținut colet:</strong> {order.items.reduce((s, i) => s + i.quantity, 0)} produse</p>
          <p><strong>Plată:</strong> {order.payment_method}</p>
          {c.notes && <p><strong>Observații:</strong> {c.notes}</p>}
        </div>
      )}

      {isInvoice && (
        <p className="mt-8 text-xs text-slate-400">
          * Completați datele fiscale reale (CUI, Reg. Com., IBAN, cotă TVA) în backend înainte de emiterea oficială.
        </p>
      )}
    </div>
  );
}
